<template>
  <div>
    <v-container>
      <h1 class="primary--text font-weight-bold pb-4">
        {{ isEditLandlordPlan ? $t("plans.update") : $t("plans.addNew") }}
      </h1>
      <ValidationObserver ref="observer" v-slot="{ handleSubmit }">
        <form @submit.prevent="handleSubmit(onSave)">
          <!-- Plan Name  -->
          <div>
            <ValidationProvider
              v-slot="{ errors }"
              rules="required"
              :name="$t('plans.name')"
            >
              <v-text-field
                outlined
                :error-messages="errors"
                dense
                :label="`${$t('plans.name')} (${$t('common.en')})`"
                v-model="planModel.planName"
                color="secondary"
              >
                <template #label>
                  <span class="red--text"><strong>* </strong></span>
                  {{ $t("plans.name") }} ({{ $t("common.en") }})
                </template>
              </v-text-field>
            </ValidationProvider>
          </div>

          <div class=" tw-grid tw-grid-cols-2 tw-gap-x-4">
            <!-- Number of Users -->
            <div>
              <ValidationProvider
                v-slot="{ errors }"
                rules="required"
                :name="$t('plans.numberOfUsers')"
              >
                <v-text-field
                  outlined
                  :error-messages="errors"
                  dense
                  :label="$t('plans.numberOfUsers')"
                  v-model="planModel.numberOfUsers"
                  type="number"
                  color="secondary"
                >
                  <template #label>
                    <span class="red--text"><strong>* </strong></span>
                    {{ $t("plans.numberOfUsers") }}
                  </template>
                </v-text-field>
              </ValidationProvider>
            </div>

            <!-- Number of Properties -->
            <div>
              <ValidationProvider
                v-slot="{ errors }"
                rules="required"
                :name="$t('plans.numberOfProperties')"
              >
                <v-text-field
                  outlined
                  :error-messages="errors"
                  dense
                  :label="$t('plans.numberOfProperties')"
                  v-model="planModel.numberOfProperties"
                  type="number"
                  color="secondary"
                >
                  <template #label>
                    <span class="red--text"><strong>* </strong></span>
                    {{ $t("plans.numberOfProperties") }}
                  </template>
                </v-text-field>
              </ValidationProvider>
            </div>

            <!-- Number of Units -->
            <div>
              <ValidationProvider
                v-slot="{ errors }"
                rules="required"
                :name="$t('plans.numberOfUnits')"
              >
                <v-text-field
                  outlined
                  :error-messages="errors"
                  dense
                  :label="$t('plans.numberOfUnits')"
                  v-model="planModel.numberOfUnits"
                  type="number"
                  color="secondary"
                >
                  <template #label>
                    <span class="red--text"><strong>* </strong></span>
                    {{ $t("plans.numberOfUnits") }}
                  </template>
                </v-text-field>
              </ValidationProvider>
            </div>

            <!-- Currency -->

            <!-- Price -->
            <div>
              <ValidationProvider
                v-slot="{ errors }"
                rules="required"
                :name="$t('plans.price')"
              >
                <v-text-field
                  outlined
                  :error-messages="errors"
                  dense
                  :label="$t('plans.price')"
                  v-model="planModel.price"
                  type="number"
                  step="0.01"
                  color="secondary"
                >
                  <template #label>
                    <span class="red--text"><strong>* </strong></span>
                    {{ $t("plans.price") }}
                  </template>
                </v-text-field>
              </ValidationProvider>
            </div>

            <!-- Is Active -->
            <div>
              <v-switch
                v-model="planModel.isActive"
                :label="$t('landlord.isActive')"
                color="primary"
              ></v-switch>
            </div>
          </div>
          <div class=" tw-w-full tw-flex tw-justify-end">
            <!-- Submit Button -->
            <v-btn
              color="primary"
              class="white--text"
              type="submit"
              width="10%"
              :loading="btnLoading"
            >
              {{ isEditLandlordPlan ? $t("common.update") : $t("common.save") }}
            </v-btn>
          </div>

          <!-- Error Message -->
          <v-col v-if="errorMessage" cols="12">
            <alert :message="errorMessage" />
          </v-col>
        </form>
      </ValidationObserver>
    </v-container>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { createLandlordPlan } from "../../methods/create_landlord_plan";
import { updateLandlordPlan } from "../../methods/update_landlord_plan";
import "@/core/methods/validation_rules";

export default {
  data() {
    return {
      btnLoading: false,
      errorMessage: null,
      planModel: {
        planName: "",
        validity: null,
        numberOfUsers: null,
        numberOfProperties: null,
        numberOfUnits: null,
        currency: "",
        price: null,
        isActive: true
      },
      currencyOptions: [{ text: "KWD", value: "KWD" }]
    };
  },
  computed: {
    ...mapGetters(["isEditLandlordPlan", "editLandlordPlan"])
  },
  watch: {
    isEditLandlordPlan: {
      immediate: true,
      handler(isEdit) {
        if (isEdit) {
          this.planModel = { ...this.editLandlordPlan };
        } else {
          this.resetForm();
        }
      }
    }
  },
  methods: {
    resetForm() {
      this.planModel = {
        planName: "",
        validity: null,
        numberOfUsers: null,
        numberOfProperties: null,
        numberOfUnits: null,
        currency: "",
        price: null,
        isActive: true
      };
      this.$refs.observer?.reset();
    },
    async onSave() {
      this.btnLoading = true;
      this.errorMessage = null;

      try {
        const input = this.$format.cloneVariable(this.planModel);
        delete input._id;
        delete input.__typename;
        input.price = parseFloat(input.price);
        input.numberOfUsers = parseInt(input.numberOfUsers);
        input.numberOfProperties = parseInt(input.numberOfProperties);
        input.numberOfUnits = parseInt(input.numberOfUnits);
        if (this.isEditLandlordPlan) {
          const response = await updateLandlordPlan({
            id: this.editLandlordPlan._id,
            input
          });

          if (response.data.updateLandlordValidityPlan) {
            this.$root.$emit("refetchLandlordPlan");
            this.$store.commit("SHOW_RIGHTSIDE_DRAWER", false);
            this.$store.commit("IS_EDIT_LANDLORD_PLAN", false);
            this.$root.$emit("snackbar", {
              snackbar: true,
              text: this.$t("common.updateSuccess"),
              color: "green"
            });
          }
        } else {
          const response = await createLandlordPlan(input);

          if (response.data.createLandlordValidityPlan) {
            this.resetForm();
            this.$root.$emit("refetchLandlordPlan");
            this.$store.commit("SHOW_RIGHTSIDE_DRAWER", false);
            this.$root.$emit("snackbar", {
              snackbar: true,
              text: this.$t("common.saveSuccess"),
              color: "green"
            });
          }
        }
      } catch (error) {
        this.errorMessage =
          error.graphQLErrors?.[0]?.message || this.$t("common.error");
      } finally {
        this.btnLoading = false;
      }
    }
  }
};
</script>
<style scoped>
.numberClass >>> input[type="number"] {
  appearance: textfield;
  -moz-appearance: textfield;
}

.numberClass >>> input::-webkit-outer-spin-button,
.numberClass >>> input::-webkit-inner-spin-button {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}
</style>
